/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { Card, CardHeader, CardContent, Grid, Link, Typography, makeStyles, IconButton, Collapse } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import BlockIcon from '@material-ui/icons/Block';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';
import UpdateIcon from '@material-ui/icons/Update';
import VisibilityIcon from '@material-ui/icons/Visibility';

import AlignmentViewer from './AlignmentViewer';
import AlternateIdEditor from './AlternateIdsEditor';
import Periods, { getEditablePeriod } from './Periods';
import { getAiringHighlights } from './apiQueries';

import { useDrmMediaUrls } from '../../hooks';

import Highlights from '../common/Highlights';
import TeamList from '../common/TeamList';
import VideoPlayer from '../common/VideoPlayer';

import SimpleChipInfo from '../common/SimpleChipInfo';
import SimpleDateInfo from '../common/SimpleDateInfo';
import SimpleImageInfo from '../common/SimpleImageInfo';
import SimpleTextInfo from '../common/SimpleTextInfo';
import StatusChip, { statusChipStyles } from '../common/StatusChip';

import { Airing, AlternateId, DRMTYPE, HLS_TRANSPORT, Period, Status } from '../../utils/types';

const DEFAULT_DURATION = 10800;
const HERO_IMG_STEM =
	'https://myriad.merlin.xfinity.eu/select/image?companyId=8656828586641685105&width=960&height=540&selectionCriteria[0].imageType=Sky_STB_HERO_HD&partnerIdentifier=sky-uk&entityId=';

const useStyles = makeStyles((theme) => ({
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		})
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	gameActionLink: {
		color: theme.palette.secondary.main,
		display: 'flex',
		marginBottom: theme.spacing(2),
		[theme.breakpoints.up('sm')]: {
			justifyContent: 'flex-end'
		},
		[theme.breakpoints.down('sm')]: {
			marginTop: theme.spacing(2)
		}
	},
	linkText: {
		marginLeft: theme.spacing(0.5)
	}
}));

export const AiringDetails: FC<AiringDetailsProps> = (props) => {
	const {
		alternateIds,
		businessLogic,
		channel,
		channelId,
		clientExclusions,
		drm = false,
		editorialHighlight,
		game,
		hidePeriodDetails,
		id,
		offsets,
		programEndTime,
		programStartTime,
		programSynopsis,
		refetchAiringDetails,
		sources,
		startTime,
		status
	} = props;

	const [alignmentModalOpen, setAlignmentModalOpen] = useState(false);
	const [sourcesCardOpen, setSourcesCardOpen] = useState(false);

	const classes = useStyles();
	const history = useHistory();
	const statusChipClasses = statusChipStyles();

	let hlsVideoUrl: string;
	let dashVideoUrl;
	if (channel?.sourceAssets?.items) {
		const hlsUrl = channel.sourceAssets.items.find(({ id, format }) => id === 'HLS' || format === 'HLS');
		if (hlsUrl) hlsVideoUrl = hlsUrl.url;

		const dashUrl = channel.sourceAssets.items.find(({ id, format }) => id === 'DASH' || format === 'DASH');
		if (dashUrl) dashVideoUrl = dashUrl.url;
	}

	const drmHlsSource = sources?.find(({ transport }): boolean => transport === HLS_TRANSPORT);

	const mediaDataUrls  = useDrmMediaUrls({drmHlsSource, status: status})

	const periodItems: Period[] = editorialHighlight?.periods.items ? getEditablePeriod(editorialHighlight?.periods.items) : [];

	const videoStart = dayjs(new Date(startTime!)).toISOString();
	const videoEnd = dayjs(new Date(startTime!))
		.add(DEFAULT_DURATION / 60, 'm')
		.toISOString();

	const isHighlightable =
		(hlsVideoUrl || (dashVideoUrl && channel?.type?.toString() === 'EXTERNAL') || (sources && drmHlsSource)) &&
		startTime &&
		game?.sport?.toLowerCase() !== 'golf';
	const hasTeams = !['golf', 'f1'].includes(game?.sport?.toLowerCase() || '');

	const merlinProgramId = alternateIds?.items
		.find((alternateId: AlternateId): boolean => alternateId.namespace === 'MERLIN_PROGRAM_ID')
		?.id.split(':')
		.pop();

	const heroImageLink = `${HERO_IMG_STEM}${merlinProgramId}`;

	const renderPlayers = (): JSX.Element => {
		if (drm && drmHlsSource && mediaDataUrls?.licenseUrl  && mediaDataUrls?.videoUrl) {
			const { protection: drmType } = drmHlsSource;

			return (
				<>
					<VideoPlayer
						drmType={drmType.toLowerCase() as DRMTYPE}
						height='400px'
						laSource={mediaDataUrls.licenseUrl}
						source={mediaDataUrls.videoUrl!}
						timeShift={false}
						videoEnd={videoEnd}
						videoStart={videoStart}
					/>
					<Alert severity='info'>
						A VPN connection is required to watch a LIVE stream.
						<br />
						DRM protected content may cease to be viewable after the LIVE stream ends.
					</Alert>
				</>
			);
		} else if (drm && drmHlsSource && !mediaDataUrls.licenseUrl) {
			return <Alert severity='warning'>DRM license aquisition in progress</Alert>;
		} else if ((!drm || !drmHlsSource) && hlsVideoUrl) {
			return (
				<>
					<VideoPlayer height='400px' source={hlsVideoUrl} timeShift={false} videoEnd={videoEnd} videoStart={videoStart} />
					<Alert severity='info'>A VPN connection is required to watch a LIVE stream</Alert>
				</>
			);
		} else {
			return <Alert severity='error'>There was an error aquiring the Video URL or Start Time</Alert>;
		}
	};

	return (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<Card>
					<CardHeader title='Airing details' />
					<CardContent>
						<Grid container spacing={2}>
							<Grid container item xs={12} md={9} spacing={3}>
								<Grid container item xs={12} lg={6} direction='column' spacing={2}>
									<SimpleTextInfo label='ID' value={id} to={`/airings/${encodeURIComponent(id)}`} />
									<SimpleTextInfo
										label='Airing Status'
										value={<StatusChip className={statusChipClasses.smallChip} status={status || 'not specified'} />}
									/>
									<SimpleTextInfo label='Channel' value={channelId} />
									<SimpleTextInfo label='Business Logic' value={businessLogic ?? ''} />
									<SimpleDateInfo label='Start Time' value={startTime} />
									<SimpleChipInfo label='Client Exclusions' value={clientExclusions} />
									{game?.id && (
										<>
											<SimpleTextInfo label='Game' value={game.id} to={`/games/${encodeURIComponent(game.id)}`} />
											{hasTeams && <TeamList teams={game?.teams?.items} />}
										</>
									)}
									{offsets?.items?.length && <SimpleTextInfo label='Offset' value={`${offsets?.items[0].offset} Seconds`} />}
									{typeof merlinProgramId !== 'undefined' && (
										<div className={classes.linkText} style={{ float: 'left' }}>
											{' '}
											<SimpleImageInfo label='Hero Image' src={heroImageLink} width={240} height={135} pathname={heroImageLink} target={'_blank'} />
										</div>
									)}
									<SimpleTextInfo label='Program Synopsis' value={programSynopsis ?? ''} />
									<SimpleDateInfo label='Program Start Time' value={programStartTime ?? ''} />
									<SimpleDateInfo label='Program End Time' value={programEndTime ?? ''} />
									{sources && sources.length > 0 && (
										<>
											<SimpleTextInfo
												label='Source Properties'
												value={
													<IconButton
														onClick={(): void => setSourcesCardOpen(!sourcesCardOpen)}
														aria-expanded={sourcesCardOpen}
														aria-label='show more'
														size='small'>
														{sourcesCardOpen ? <ExpandLessIcon fontSize='inherit' /> : <ExpandMoreIcon fontSize='inherit' />}
													</IconButton>
												}
											/>
											<Collapse in={sourcesCardOpen} timeout='auto' unmountOnExit>
												{sources.map((_, idx) => (
													<div style={{ marginBottom: idx !== sources.length - 1 ? '1.5rem' : undefined, marginLeft: '0.5rem' }} key={idx}>
														{sources[idx].url && <SimpleTextInfo label='Url' value={sources[idx].url} />}
														{sources[idx].transport && <SimpleTextInfo label='Transport' value={sources[idx].transport} />}
														{sources[idx].protection && <SimpleTextInfo label='Protection' value={sources[idx].protection} />}
														{sources[idx].propositionContentId && <SimpleTextInfo label='Content ID' value={sources[idx].propositionContentId} />}
														{sources[idx].providerVariantId && <SimpleTextInfo label='Provider Variant ID' value={sources[idx].providerVariantId} />}
													</div>
												))}
											</Collapse>
										</>
									)}
								</Grid>
								<Grid container item xs={12} lg={6} direction='column' spacing={1}>
									{renderPlayers()}
								</Grid>
							</Grid>
							<Grid container item xs={12} md={3} direction='column'>
								<Link component={RouterLink as any} to={`/airings/${id}/edit`} className={classes.gameActionLink}>
									<EditIcon />
									<Typography className={classes.linkText}>Edit Airing details</Typography>
								</Link>
								{isHighlightable && (
									<>
										<Link component={RouterLink as any} to={`/airings/${id}/compose-highlight`} className={classes.gameActionLink}>
											<PlaylistAddIcon />
											<Typography className={classes.linkText}>Compose Highlight</Typography>
										</Link>
										<Link component={RouterLink as any} to={`/airings/${id}/editorial-highlight`} className={classes.gameActionLink}>
											<MovieCreationIcon />
											<Typography className={classes.linkText}>Editorial Clips</Typography>
										</Link>
										<Link component={RouterLink as any} to={`/airings/${id}/excluded-clips`} className={classes.gameActionLink}>
											<BlockIcon />
											<Typography className={classes.linkText}>Excluded Clips</Typography>
										</Link>
									</>
								)}
								<Link component={RouterLink as any} to={`/airings/${id}/latest-highlight`} className={classes.gameActionLink}>
									<UpdateIcon />
									<Typography className={classes.linkText}>Latest Highlight</Typography>
								</Link>
								{status !== Status.SCHEDULED_WITH_MAF && (
									<Link
										className={classes.gameActionLink}
										onClick={(): void => {
											setAlignmentModalOpen(true);
										}}
										href='#'>
										<VisibilityIcon />
										<Typography className={classes.linkText}>View Alignment</Typography>
									</Link>
								)}
							</Grid>
						</Grid>
						<AlignmentViewer id={id} sport={game?.sport ?? ''} open={alignmentModalOpen} setOpen={setAlignmentModalOpen} icon={<VisibilityIcon />} />
					</CardContent>
				</Card>
			</Grid>
			<Grid item xs={12}>
				<AlternateIdEditor alternateIds={alternateIds?.items || []} airingId={id} refetchAiringDetails={refetchAiringDetails} />
			</Grid>
			<Grid item xs={12}>
				<Highlights id={id} query={getAiringHighlights} queryPath='getAiring' hideFilter={true} />
			</Grid>
			<Grid item xs={12}>
				<Periods
					title='Editorial Clips'
					sport={game?.sport}
					periods={periodItems ?? []}
					hideDetails={hidePeriodDetails}
					teams={game?.teams?.items ?? null}
					onEdit={(): void => history.push(`/airings/${id}/editorial-highlight`)}
				/>
			</Grid>
		</Grid>
	);
};

type AiringDetailsProps = Airing & { hidePeriodDetails?: boolean | null } & { refetchAiringDetails: () => void };

export default AiringDetails;
